// <WorkspaceIndicator> — top-bar workspace pill for the appstore surface.
//
// - Single-tenant alias: renders a static pill.
// - Multi-tenant alias (tenant_count > 1): renders a button with a "Switch
//   workspace" dropdown that links to /tenant-picker.
//
// Mounted from <AdminLayout> only when the WorkspaceProvider has resolved
// a tenant; otherwise the topbar shows nothing for the appstore surface.

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useWorkspace } from "@/workspace";

export function WorkspaceIndicator(): JSX.Element | null {
  const { workspace } = useWorkspace();
  const [open, setOpen] = useState<boolean>(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  if (!workspace) {
    return null;
  }

  if (workspace.tenant_count <= 1) {
    return (
      <span
        className="reb-workspace-pill"
        title={workspace.tenant_id}
        aria-label={`Active workspace: ${workspace.tenant_name}`}
      >
        {workspace.tenant_name}
      </span>
    );
  }

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        className="reb-workspace-pill__button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {workspace.tenant_name}
        <span aria-hidden="true">&#x25BE;</span>
      </button>
      {open && (
        <div className="reb-workspace-pill__menu" role="menu">
          <Link
            to="/tenant-picker"
            className="reb-workspace-pill__menu-item"
            role="menuitem"
          >
            Switch workspace
          </Link>
          <Link
            to="/admin/tenant"
            className="reb-workspace-pill__menu-item"
            role="menuitem"
          >
            Workspace settings
          </Link>
        </div>
      )}
    </div>
  );
}
